import { handleActions, combineActions } from 'redux-actions'
import { combineReducers } from 'redux-immutable'
import { Map } from 'immutable'
import * as actions from './actions'
import { AgentSettings } from './models'

import { buildMappings } from './helpers'

const orgSettings = handleActions(
  {
    [combineActions(
      actions.fetchAssistantOrgSettingsPending,
      actions.fetchAssistantOrgSettingsSuccess,
      actions.fetchAssistantOrgSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.createAssistantOrgSettingsPending,
      actions.createAssistantOrgSettingsSuccess,
      actions.createAssistantOrgSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.patchAssistantOrgSettingsPending,
      actions.patchAssistantOrgSettingsSuccess,
      actions.patchAssistantOrgSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.fetchAssistantOrgSettingsSuccess,
      actions.createAssistantOrgSettingsSuccess,
      actions.patchAssistantOrgSettingsSuccess
    )]: (state, action) => {
      return state.set('data', action.payload)
    },
  },
  Map()
)

const agentSettings = handleActions(
  {
    [combineActions(
      actions.fetchAssistantAgentSettingsPending,
      actions.fetchAssistantAgentSettingsSuccess,
      actions.fetchAssistantAgentSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.createAssistantAgentSettingsPending,
      actions.createAssistantAgentSettingsSuccess,
      actions.createAssistantAgentSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.patchAssistantAgentSettingsPending,
      actions.patchAssistantAgentSettingsSuccess,
      actions.patchAssistantAgentSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.fetchAssistantAgentSettingsSuccess,
      actions.createAssistantAgentSettings,
      actions.patchAssistantAgentSettingsSuccess
    )]: (state, action) => {
      return state.set('data', action.payload)
    },
  },
  new Map({
    data: AgentSettings(),
    status: null,
  })
)

const salesforceMappings = handleActions(
  {
    [combineActions(
      actions.fetchSalesforceSettingsPending,
      actions.fetchSalesforceSettingsSuccess,
      actions.fetchSalesforceSettingsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(actions.fetchSalesforceSettingsSuccess)]: (state, action) => {
      // Turn massive SFDC settings lists into usable formats
      const leadFields = []
      const contactFields = []
      const eventFields = []
      const taskFields = []

      const leadFieldValues = {}
      const contactFieldValues = {}
      const eventFieldValues = {}
      const taskFieldValues = {}

      buildMappings(action.payload.LEAD, leadFields, leadFieldValues)
      buildMappings(action.payload.CONTACT, contactFields, contactFieldValues)
      buildMappings(action.payload.EVENT, eventFields, eventFieldValues)
      buildMappings(action.payload.TASK, taskFields, taskFieldValues)
      return state
        .setIn(['data', 'LEAD', 'leadFields'], leadFields)
        .setIn(['data', 'LEAD', 'leadFieldValues'], leadFieldValues)
        .setIn(['data', 'CONTACT', 'contactFields'], contactFields)
        .setIn(['data', 'CONTACT', 'contactFieldValues'], contactFieldValues)
        .setIn(['data', 'EVENT', 'eventFields'], eventFields)
        .setIn(['data', 'EVENT', 'eventFieldValues'], eventFieldValues)
        .setIn(['data', 'TASK', 'taskFields'], taskFields)
        .setIn(['data', 'TASK', 'taskFieldValues'], taskFieldValues)
    },
  },
  Map()
)

const editingOrgSettings = handleActions(
  {
    [actions.handleEditConfig]: (state, { payload }) => {
      return state.set(payload.settingName, payload.value)
    },
  },
  Map()
)

const editingAgentSettings = handleActions(
  {
    [combineActions(
      actions.fetchAssistantAgentSettingsSuccess,
      actions.patchAssistantAgentSettingsSuccess
    )]: (state, { payload }) => {
      return payload.get('taskConfigMap')
    },
    [actions.onCancel]: (state, { payload }) => {
      return payload
    },
    [actions.onUpdate]: (state, { payload }) => {
      return state.set(payload.settingName, payload.newSettings)
    },
    [actions.onWarmLeadUpdate]: (state, { payload }) => {
      const newCancelSettings = state
        .get('MEETING_CANCELLED')
        .set('on', payload.newSettings.get('on'))
        .set('delayTime', payload.newSettings.get('delayTime'))
      const newRescheduledSettings = state
        .get('MEETING_RESCHEDULED')
        .set('on', payload.newSettings.get('on'))
        .set('delayTime', payload.newSettings.get('delayTime'))
      return state
        .set('MEETING_CANCELLED', newCancelSettings)
        .set('MEETING_RESCHEDULED', newRescheduledSettings)
    },
    [actions.onWarmLeadUpdateTemplate]: (state, { payload }) => {
      const cancelSettings = payload.MEETING_CANCELLED
      const rescheduleSettings = payload.MEETING_RESCHEDULED
      const newCancelSettings = state
        .get('MEETING_CANCELLED')
        .set('emailSubject', cancelSettings.get('emailSubject'))
        .set('emailBody', cancelSettings.get('emailBody'))
      const newRescheduledSettings = state
        .get('MEETING_RESCHEDULED')
        .set('emailSubject', rescheduleSettings.get('emailSubject'))
        .set('emailBody', rescheduleSettings.get('emailBody'))
      return state
        .set('MEETING_CANCELLED', newCancelSettings)
        .set('MEETING_RESCHEDULED', newRescheduledSettings)
    },
  },
  Map()
)

const personalizationTags = handleActions(
  {
    [combineActions(
      actions.fetchPersonalizationTagsPending,
      actions.fetchPersonalizationTagsSuccess,
      actions.fetchPersonalizationTagsFailed
    )]: (state, action) => {
      return state.set('status', action.type)
    },

    [combineActions(
      actions.fetchPersonalizationTagsSuccess,
    )]: (state, action) => {
      return state.set('data', action.payload)
    },
  },
  Map()
)

const sendingTestEmailStatus = handleActions(
  {
    [combineActions(
      actions.sendTestEmailPending,
      actions.sendTestEmailSuccess,
      actions.sendTestEmailFailed
    )]: (state, action) => {
      return action.type
    }
  }, null
)

export default combineReducers({
  orgSettings,
  agentSettings,
  salesforceMappings,
  editingAgentSettings,
  editingOrgSettings,
  personalizationTags,
  sendingTestEmailStatus
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/assistantSettings/reducer.js